// 오디오 clip 을 Remotion timeline item + asset 으로 변환
import { Caption } from '@remotion/captions';
import {
  AlignWord,
  AudioClipInfo,
  CaptionClipInfo,
  CLIP_TYPES,
  ClipInfo,
  ClipType,
  ContentClipInfo,
  ImageClipInfo,
  isVideoClip,
  isTitleClip,
  TitleClipInfo,
  VideoClipInfo,
} from '@/shared/apis/editor/clip-types';
import { isCaptionHighlightTimingEligible } from '@/shared/lib/captions/caption-timing-quality';
import { buildCaptionsFromTranslationCues } from '@/shared/lib/captions/caption-translation-cues';
import {
  ContentInfo,
  EditorDataResponse,
} from '@/shared/apis/scriptSnippetApi';
import {
  AudioItem,
  AudioItemRenderMeta,
} from '@/features/remotion-editor/editor/items/audio/audio-item-type';
import {
  AssetState,
  AudioAsset,
  CaptionAsset,
  CaptionMetadataType,
  EditorStarterAsset,
  ImageAsset,
  VideoAsset,
} from '@/features/remotion-editor/editor/assets/assets';
import { generateRandomId } from '@/features/remotion-editor/editor/utils/generate-random-id';
import { DtoProjectType, DtoResolution } from '@/shared/constants';
import { ReframeMeta } from '@/shared/apis/editor/reframe-types';
import {
  AVATAR_SEGMENT_SOURCE_ROLE,
  VideoItem,
  VideoItemRenderMeta,
} from '@/features/remotion-editor/editor/items/video/video-item-type';
import { calculateMediaDimensionsForCanvas } from '@/features/remotion-editor/editor/utils/dimension-utils';
import { MIN_VOLUME_DB } from '@/features/remotion-editor/editor/utils/decibels';
import {
  CaptionItemRenderMeta,
  WORD_BREAK_STYLE,
  defaultTextShadow,
  TextItem,
  TitleItemRenderMeta,
} from '@/features/remotion-editor/editor/items/text/text-item-type';
import {
  createTextItem,
  DEFAULT_FONT_SIZE,
} from '@/features/remotion-editor/editor/items/text/create-text-item-wkwk';
import {
  EditorItemType,
  EditorStarterItem,
} from '@/features/remotion-editor/editor/items/item-type';
import { TrackType } from '@/features/remotion-editor/editor/state/types';
import { TextStyleConfig } from '@/features/remotion-editor/editor/state/hydrate/hydrate-editor-state';
import { getZoneForItemType } from '@/features/remotion-editor/editor/utils/zone-utils';
import { stringSeemsRightToLeft } from '@/features/remotion-editor/editor/utils/text/right-to-left';
import { CaptionsItem } from '@/features/remotion-editor/editor/items/captions/captions-item-type';
import { DEFAULT_FONT_FAMILY } from '@/features/brand-template/model/brand-template';
import {
  ImageItem,
  ImageItemRenderMeta,
} from '@/features/remotion-editor/editor/items/image/image-item-type';
import { CodeItem } from '@/features/remotion-editor/editor/items/code/code-item-type';
import type { TransitionItem } from '@/features/remotion-editor/editor/items/transition/transition-item-type';

export type ContentInfoMap = Record<number, ContentInfo>;

export const enrichSavedVideoItemsWithSourceRoles = ({
  items,
  clipInfos,
}: {
  items: Record<string, EditorStarterItem>;
  clipInfos: ClipInfo[];
}): Record<string, EditorStarterItem> => {
  const avatarSegmentClipIds = new Set(
    clipInfos
      .filter(isVideoClip)
      .filter(
        (clip) =>
          clip.hasAlpha === true &&
          clip.sourceRole === AVATAR_SEGMENT_SOURCE_ROLE,
      )
      .map((clip) => String(clip.id)),
  );
  if (avatarSegmentClipIds.size === 0) return items;

  return Object.fromEntries(
    Object.entries(items).map(([itemId, item]) => {
      if (
        item.type !== 'video' ||
        item.hasAlpha !== true ||
        typeof item.sourceRole === 'string'
      ) {
        return [itemId, item];
      }
      const clipId = item.renderMeta?.id;
      if (clipId == null || !avatarSegmentClipIds.has(String(clipId))) {
        return [itemId, item];
      }
      return [itemId, { ...item, sourceRole: AVATAR_SEGMENT_SOURCE_ROLE }];
    }),
  );
};

interface ClipLayout {
  left: number;
  top: number;
  width: number;
  height: number;
}

type SourceVideoDimensionClip = ContentClipInfo & {
  source_video_width?: number;
  source_video_height?: number;
  videoWidth?: number;
  videoHeight?: number;
  video_width?: number;
  video_height?: number;
  sourceWidth?: number;
  sourceHeight?: number;
  source_width?: number;
  source_height?: number;
  naturalWidth?: number;
  naturalHeight?: number;
  natural_width?: number;
  natural_height?: number;
};

const MEDIA_RATIO_SNAP_TOLERANCE = 0.03;
const KNOWN_MEDIA_DIMENSIONS = [
  { width: 1920, height: 1080, ratio: 16 / 9 },
  { width: 1080, height: 1920, ratio: 9 / 16 },
  { width: 1080, height: 1080, ratio: 1 },
  { width: 1080, height: 1350, ratio: 4 / 5 },
  { width: 1440, height: 1080, ratio: 4 / 3 },
  { width: 1080, height: 1440, ratio: 3 / 4 },
] as const;

const readPositiveDimension = (...values: Array<number | null | undefined>) =>
  values.find(
    (value): value is number =>
      typeof value === 'number' && Number.isFinite(value) && value > 0,
  );

const normalizeMediaDimensionsForEditor = ({
  width,
  height,
  compositionWidth,
  compositionHeight,
}: {
  width?: number | null;
  height?: number | null;
  compositionWidth: number;
  compositionHeight: number;
}) => {
  const sourceWidth = readPositiveDimension(width, compositionWidth) ?? 1;
  const sourceHeight = readPositiveDimension(height, compositionHeight) ?? 1;
  const sourceRatio = sourceWidth / sourceHeight;

  const matchedDimension = KNOWN_MEDIA_DIMENSIONS.reduce<
    (typeof KNOWN_MEDIA_DIMENSIONS)[number] | null
  >((best, dimension) => {
    if (!best) return dimension;
    return Math.abs(dimension.ratio - sourceRatio) <
      Math.abs(best.ratio - sourceRatio)
      ? dimension
      : best;
  }, null);

  if (
    !matchedDimension ||
    Math.abs(matchedDimension.ratio - sourceRatio) >= MEDIA_RATIO_SNAP_TOLERANCE
  ) {
    return { width: sourceWidth, height: sourceHeight };
  }

  const compositionRatio = compositionWidth / compositionHeight;
  if (
    Math.abs(compositionRatio - matchedDimension.ratio) <
    MEDIA_RATIO_SNAP_TOLERANCE
  ) {
    return { width: compositionWidth, height: compositionHeight };
  }

  return {
    width: matchedDimension.width,
    height: matchedDimension.height,
  };
};

const readClipLayout = (
  clip: ContentClipInfo | CaptionClipInfo,
): ClipLayout | null => {
  const { left, top, width, height } = clip;
  if (
    typeof left !== 'number' ||
    typeof top !== 'number' ||
    typeof width !== 'number' ||
    typeof height !== 'number'
  ) {
    return null;
  }
  if (
    !Number.isFinite(left) ||
    !Number.isFinite(top) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }
  return {
    left: Math.round(left),
    top: Math.round(top),
    width: Math.round(width),
    height: Math.round(height),
  };
};

// sceneId 와 linkId 조합으로 고유 키를 생성해 동일 씬 내에서만 linkId 연계를 유지한다
export const createLinkSceneKey = ({
  linkId,
  sceneId,
}: {
  linkId?: number | null;
  sceneId?: number | null;
}): string | null => {
  if (!Number.isFinite(linkId) || !Number.isFinite(sceneId)) {
    return null;
  }

  return `${sceneId}:${linkId}`;
};

// 텍스트 트랙 배치를 위한 기본 비율 값
export const CAPTION_Y_RATIO = 0.75;
export const TITLE_Y_RATIO = 0.2;
const DEFAULT_CAPTION_MAX_LINES = 2;
const DEFAULT_CAPTION_PAGE_DURATION_MS = 60 * 1000;
const DEFAULT_CAPTION_HIGHLIGHT_COLOR = '#39E508';
const MIN_CAPTION_WORD_DURATION_MS = 10;

const DEFAULT_TEXT_LINE_HEIGHT = 1.2;
const DEFAULT_TEXT_LETTER_SPACING = 0;
const AGENT_WORKFLOW_CODE_ITEM_PAYLOAD_KEY = 'agentWorkflowRemotionCodeItems';
const AGENT_WORKFLOW_EDITOR_SOURCE_ASSETS_KEY =
  'agentWorkflowEditorSourceAssets';
const AGENT_WORKFLOW_EDITOR_SOURCE_ITEMS_KEY = 'agentWorkflowEditorSourceItems';
const AGENT_WORKFLOW_EDITOR_SOURCE_TRACKS_KEY =
  'agentWorkflowEditorSourceTracks';
const AGENT_WORKFLOW_EDITOR_SOURCE_TRANSITIONS_KEY =
  'agentWorkflowEditorSourceTransitions';

// 글자별 상대 폭을 추정해 fontSize 대비 비율 값을 계산한다
const estimateCharUnit = (char: string, letterSpacingUnit: number): number => {
  if (!char) return letterSpacingUnit;

  if (char === '\n') return 0;
  if (char === ' ') return 0.4 + letterSpacingUnit;

  const code = char.codePointAt(0);
  if (!code) return 0.7 + letterSpacingUnit;

  // 한글 및 CJK 문자는 가로폭이 크므로 1.0 단위로 취급한다
  if (
    (code >= 0x1100 && code <= 0x11ff) ||
    (code >= 0x3130 && code <= 0x318f) ||
    (code >= 0xac00 && code <= 0xd7af) ||
    (code >= 0x4e00 && code <= 0x9fff) ||
    (code >= 0xa960 && code <= 0xa97f) ||
    (code >= 0xff01 && code <= 0xff60) ||
    (code >= 0xffe0 && code <= 0xffe6)
  ) {
    return 1 + letterSpacingUnit;
  }

  if (code >= 0x1f000) {
    // 이모지는 실제 폭이 넓어 여유 있게 잡아준다
    return 1.2 + letterSpacingUnit;
  }

  if (/[A-Z]/u.test(char)) return 0.7 + letterSpacingUnit;
  if (/[a-z0-9]/u.test(char)) return 0.6 + letterSpacingUnit;
  if (/[.,;:!?"'`]/u.test(char)) return 0.35 + letterSpacingUnit;
  if (/[-/\\]/u.test(char)) return 0.5 + letterSpacingUnit;

  return 0.75 + letterSpacingUnit;
};

// 드래그 리사이즈 등 빈번한 호출에서 DOM 생성/삭제 비용을 줄이기 위해
// probe 요소를 재사용한다
let cachedProbe: HTMLDivElement | null = null;

const getProbe = (): HTMLDivElement => {
  if (cachedProbe && cachedProbe.isConnected) return cachedProbe;

  const probe = document.createElement('div');
  probe.style.position = 'absolute';
  probe.style.visibility = 'hidden';
  probe.style.pointerEvents = 'none';
  probe.style.whiteSpace = 'pre-wrap';
  probe.style.wordBreak = WORD_BREAK_STYLE.wordBreak;
  probe.style.overflowWrap = WORD_BREAK_STYLE.overflowWrap;
  probe.style.left = '0';
  probe.style.top = '0';
  probe.style.opacity = '0';
  probe.style.zIndex = '-1';
  document.body.appendChild(probe);
  cachedProbe = probe;
  return probe;
};

export const measureAutoWrappedHeight = ({
  text,
  width,
  fontSize,
  lineHeight,
  letterSpacing,
  fontFamily,
  fontStyle,
}: {
  text: string;
  width: number;
  fontSize: number;
  lineHeight: number;
  letterSpacing: number;
  fontFamily: string;
  fontStyle: { variant: string; weight: string };
}): number | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  const probe = getProbe();
  probe.style.width = `${width}px`;
  probe.style.fontSize = `${fontSize}px`;
  probe.style.lineHeight = String(lineHeight);
  probe.style.letterSpacing = `${letterSpacing}px`;
  probe.style.fontFamily = fontFamily;
  probe.style.fontWeight = fontStyle.weight;
  probe.style.fontStyle = fontStyle.variant.toLowerCase().includes('italic')
    ? 'italic'
    : 'normal';
  probe.textContent = text;

  const measured = probe.getBoundingClientRect().height;
  return Number.isFinite(measured) ? Math.max(1, Math.round(measured)) : null;
};

const msToSeconds = (value?: number | null) =>
  typeof value === 'number' ? value / 1000 : 0;
const msToCeilFrame = (value: number, fps: number) =>
  Math.ceil((value * fps) / 1000);
const toValidNumber = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const guessMimeType = (clipType: ClipType) => {
  switch (clipType) {
    case CLIP_TYPES.AUDIO:
      return 'audio/mpeg';
    case CLIP_TYPES.VIDEO:
      return 'video/mp4';
    case CLIP_TYPES.GIF:
      return 'image/gif';
    case CLIP_TYPES.IMAGE:
      return 'image/png';
    default:
      return 'binary/octet-stream';
  }
};

// Convert server-provided millisecond ranges to Remotion frame ranges without trimming media early.
const calculateFrameRange = ({
  fps,
  startMs,
  endMs,
  fallbackDurationInSeconds,
}: {
  fps: number;
  startMs?: number | null;
  endMs?: number | null;
  fallbackDurationInSeconds?: number | null;
}): { from: number; durationInFrames: number; endFrame: number } => {
  const safeStartMs =
    typeof startMs === 'number' && Number.isFinite(startMs) ? startMs : 0;
  const from = Math.max(0, msToCeilFrame(safeStartMs, fps));

  const safeEndMs = toValidNumber(endMs);
  const timelineEndFrame =
    safeEndMs !== null ? Math.max(0, msToCeilFrame(safeEndMs, fps)) : null;
  const timelineDuration =
    timelineEndFrame !== null ? timelineEndFrame - from : null;

  const hasFallbackDuration =
    typeof fallbackDurationInSeconds === 'number' &&
    Number.isFinite(fallbackDurationInSeconds);
  const fallbackDurationFrames = hasFallbackDuration
    ? Math.ceil((fallbackDurationInSeconds as number) * fps)
    : null;

  const durationInFrames =
    timelineDuration !== null && timelineDuration > 0
      ? timelineDuration
      : fallbackDurationFrames !== null && fallbackDurationFrames > 0
        ? fallbackDurationFrames
        : 1;

  const endFrame =
    timelineEndFrame !== null && timelineEndFrame > from
      ? timelineEndFrame
      : from + durationInFrames;

  return { from, durationInFrames, endFrame };
};

export const normalizeContentInfos = (
  editorData?: EditorDataResponse,
): ContentInfo[] => {
  // v1.0(camelCase)와 v2.0(snake_case) 모두 대응
  const raw =
    editorData?.contentInfos ??
    (editorData as EditorDataResponse | undefined)?.content_infos ??
    [];
  return Array.isArray(raw) ? raw : [];
};

export const normalizeClipInfos = (
  editorData?: EditorDataResponse,
): ClipInfo[] => {
  const raw =
    editorData?.clipInfos ??
    (editorData as EditorDataResponse | undefined)?.clip_infos ??
    [];
  return Array.isArray(raw) ? raw : [];
};

export const createFilename = (contentId: number, info?: ContentInfo) => {
  if (!info?.name) {
    return `${contentId}`;
  }
  return `${contentId}-${info.name}`;
};

export const isVideoAsset = (asset: EditorStarterAsset): asset is VideoAsset =>
  asset.type === 'video';
export const isAudioAsset = (asset: EditorStarterAsset): asset is AudioAsset =>
  asset.type === 'audio';

export const createAssetStatus = (asset?: EditorStarterAsset): AssetState => {
  return asset?.remoteUrl
    ? { type: 'uploaded' }
    : {
        type: 'pending-upload',
      };
};

export const mergeAssets = (
  existing: EditorStarterAsset | undefined,
  incoming: EditorStarterAsset,
): EditorStarterAsset => {
  if (!existing) {
    return incoming;
  }

  if (existing.type !== incoming.type) {
    return incoming;
  }

  if (isVideoAsset(existing) && isVideoAsset(incoming)) {
    return {
      ...existing,
      ...incoming,
      durationInSeconds: Math.max(
        existing.durationInSeconds,
        incoming.durationInSeconds,
      ),
      width: incoming.width ?? existing.width,
      height: incoming.height ?? existing.height,
      hasAudioTrack: incoming.hasAudioTrack ?? existing.hasAudioTrack,
      remoteUrl: incoming.remoteUrl ?? existing.remoteUrl,
      remoteFileKey: incoming.remoteFileKey ?? existing.remoteFileKey,
      size: incoming.size || existing.size,
      mimeType: incoming.mimeType || existing.mimeType,
    };
  }

  if (isAudioAsset(existing) && isAudioAsset(incoming)) {
    return {
      ...existing,
      ...incoming,
      durationInSeconds: Math.max(
        existing.durationInSeconds,
        incoming.durationInSeconds,
      ),
      remoteUrl: incoming.remoteUrl ?? existing.remoteUrl,
      remoteFileKey: incoming.remoteFileKey ?? existing.remoteFileKey,
      size: incoming.size || existing.size,
      mimeType: incoming.mimeType || existing.mimeType,
    };
  }

  return incoming;
};

export const createTrack = (
  items: EditorStarterItem[],
  type: EditorItemType,
): TrackType | null => {
  if (items.length === 0) return null;
  const sorted = [...items].sort((a, b) => a.from - b.from);
  const isMainTrackType =
    type === 'image' ||
    type === 'video' ||
    type === 'caption' ||
    type === 'audio';
  return {
    id: generateRandomId(),
    items: sorted.map((item) => item.id),
    hidden: false,
    muted: false,
    locked: false,
    magnetic: false,
    zone: getZoneForItemType(type),
    metadata: {
      isMainTrack: isMainTrackType,
      trackType: isMainTrackType ? 'main' : null,
      type,
    },
  };
};

const readFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readStringValue = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const isRecordValue = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

type EditorSourceState = {
  assets: Record<string, EditorStarterAsset>;
  items: Record<string, EditorStarterItem>;
  tracks: TrackType[];
  durationInFrames: number;
  backgroundColor?: string;
  transitions: Record<string, TransitionItem>;
};

interface NormalizeTimelineTracksForLayeringParams {
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
  transitions?: Record<string, { trackId: string }>;
}

const TRACK_LANE_OVERLAP_TOLERANCE_FRAMES = 1;
const TRACK_LANE_ID_SUFFIX_PATTERN = /-overlap-\d+$/;

const getItemEndFrame = (item: EditorStarterItem) =>
  item.from + item.durationInFrames;

const doItemsOverlap = (first: EditorStarterItem, second: EditorStarterItem) =>
  first.from < getItemEndFrame(second) - TRACK_LANE_OVERLAP_TOLERANCE_FRAMES &&
  second.from < getItemEndFrame(first) - TRACK_LANE_OVERLAP_TOLERANCE_FRAMES;

const hasOverlappingItems = (
  itemIds: string[],
  items: Record<string, EditorStarterItem>,
) => {
  for (let firstIndex = 0; firstIndex < itemIds.length; firstIndex += 1) {
    const firstItem = items[itemIds[firstIndex]!];
    if (!firstItem) continue;

    for (
      let secondIndex = firstIndex + 1;
      secondIndex < itemIds.length;
      secondIndex += 1
    ) {
      const secondItem = items[itemIds[secondIndex]!];
      if (secondItem && doItemsOverlap(firstItem, secondItem)) {
        return true;
      }
    }
  }

  return false;
};

const hasTrackTransitions = (
  trackId: string,
  transitions?: Record<string, { trackId: string }>,
) =>
  transitions
    ? Object.values(transitions).some(
        (transition) => transition.trackId === trackId,
      )
    : false;

const getTrackItemType = (
  track: TrackType,
  items: Record<string, EditorStarterItem>,
): EditorItemType | undefined =>
  track.metadata?.type ??
  track.items.map((itemId) => items[itemId]?.type).find(Boolean);

const shouldPackTrackIntoLanes = (
  track: TrackType,
  items: Record<string, EditorStarterItem>,
) => {
  const type = getTrackItemType(track, items);
  return type === 'image' || type === 'text';
};

const hasSameTrackLayerProperties = (first: TrackType, second: TrackType) =>
  first.hidden === second.hidden &&
  first.muted === second.muted &&
  first.locked === second.locked &&
  first.magnetic === second.magnetic &&
  first.zone === second.zone &&
  first.metadata?.isMainTrack === second.metadata?.isMainTrack &&
  first.metadata?.trackType === second.metadata?.trackType &&
  first.metadata?.kind === second.metadata?.kind;

const getBaseLayerTrackId = (trackId: string) =>
  trackId.replace(TRACK_LANE_ID_SUFFIX_PATTERN, '');

const shouldMergeGeneratedImageTrackGroup = (
  first: TrackType,
  second: TrackType,
) =>
  getBaseLayerTrackId(first.id) === getBaseLayerTrackId(second.id) &&
  (TRACK_LANE_ID_SUFFIX_PATTERN.test(first.id) ||
    TRACK_LANE_ID_SUFFIX_PATTERN.test(second.id));

const packTrackItemsIntoLanes = (
  itemIds: string[],
  items: Record<string, EditorStarterItem>,
) => {
  const lanes: string[][] = [];

  itemIds.forEach((itemId) => {
    const item = items[itemId];
    if (!item) return;

    const targetLane = lanes.find((lane) =>
      lane.every((laneItemId) => {
        const laneItem = items[laneItemId];
        return !laneItem || !doItemsOverlap(item, laneItem);
      }),
    );

    if (targetLane) {
      targetLane.push(itemId);
      return;
    }

    lanes.push([itemId]);
  });

  return lanes;
};

const createLayeredTracks = (
  baseTrack: TrackType,
  lanes: string[][],
  reverseLaneOrder: boolean,
): TrackType[] => {
  const orderedLanes = reverseLaneOrder ? lanes.slice().reverse() : lanes;

  return orderedLanes.map((lane, index) => ({
    ...baseTrack,
    id: index === 0 ? baseTrack.id : `${baseTrack.id}-overlap-${index + 1}`,
    items: lane,
  }));
};

const normalizeSingleTrackForLayering = ({
  track,
  items,
  transitions,
}: {
  track: TrackType;
  items: Record<string, EditorStarterItem>;
  transitions?: Record<string, { trackId: string }>;
}): TrackType[] => {
  const type = getTrackItemType(track, items);

  if (
    !shouldPackTrackIntoLanes(track, items) ||
    track.items.length <= 1 ||
    hasTrackTransitions(track.id, transitions) ||
    !hasOverlappingItems(track.items, items)
  ) {
    return [track];
  }

  return createLayeredTracks(
    track,
    packTrackItemsIntoLanes(track.items, items),
    type === 'image',
  );
};

const normalizeImageTrackGroupForLayering = (
  imageTracks: TrackType[],
  items: Record<string, EditorStarterItem>,
): TrackType[] => {
  if (imageTracks.length === 0) return [];

  const baseTrack = imageTracks[0]!;
  const itemIds = imageTracks
    .flatMap((track) => track.items)
    .filter((itemId) => items[itemId])
    .sort((firstId, secondId) => {
      const first = items[firstId]!;
      const second = items[secondId]!;
      return first.from - second.from;
    });

  if (itemIds.length === 0) return [];

  return createLayeredTracks(
    {
      ...baseTrack,
      items: itemIds,
    },
    packTrackItemsIntoLanes(itemIds, items),
    true,
  );
};

export const normalizeTimelineTracksForLayering = ({
  tracks,
  items,
  transitions,
}: NormalizeTimelineTracksForLayeringParams): TrackType[] => {
  const normalizedTracks: TrackType[] = [];
  let pendingImageTracks: TrackType[] = [];

  const flushPendingImageTracks = () => {
    normalizedTracks.push(
      ...normalizeImageTrackGroupForLayering(pendingImageTracks, items),
    );
    pendingImageTracks = [];
  };

  tracks.forEach((track) => {
    const type = getTrackItemType(track, items);
    const firstPendingImageTrack = pendingImageTracks[0];
    const isMergeableImageTrack =
      type === 'image' && !hasTrackTransitions(track.id, transitions);
    const canGroupImageTrack =
      isMergeableImageTrack &&
      (!firstPendingImageTrack ||
        (hasSameTrackLayerProperties(firstPendingImageTrack, track) &&
          shouldMergeGeneratedImageTrackGroup(firstPendingImageTrack, track)));

    if (canGroupImageTrack) {
      pendingImageTracks.push(track);
      return;
    }

    flushPendingImageTracks();

    if (isMergeableImageTrack) {
      pendingImageTracks.push(track);
      return;
    }

    normalizedTracks.push(
      ...normalizeSingleTrackForLayering({
        track,
        items,
        transitions,
      }),
    );
  });

  flushPendingImageTracks();

  return normalizedTracks;
};

export const createEditorStateFromEditorSource = ({
  editorData,
}: {
  editorData?: EditorDataResponse;
}): EditorSourceState | null => {
  const payload = editorData?.clipEditorInfos?.payload as
    | Record<string, unknown>
    | undefined;
  const assetSeeds = payload?.[AGENT_WORKFLOW_EDITOR_SOURCE_ASSETS_KEY];
  const itemSeeds = payload?.[AGENT_WORKFLOW_EDITOR_SOURCE_ITEMS_KEY];
  const trackSeeds = payload?.[AGENT_WORKFLOW_EDITOR_SOURCE_TRACKS_KEY];
  const transitionSeeds =
    payload?.[AGENT_WORKFLOW_EDITOR_SOURCE_TRANSITIONS_KEY];

  if (!isRecordValue(assetSeeds) || !isRecordValue(itemSeeds)) {
    return null;
  }

  const assets: Record<string, EditorStarterAsset> = {};
  Object.entries(assetSeeds).forEach(([key, value]) => {
    if (!isRecordValue(value)) return;
    const type = readStringValue(value.type);
    const id = readStringValue(value.id) ?? key;
    if (
      !id ||
      (type !== 'image' &&
        type !== 'video' &&
        type !== 'gif' &&
        type !== 'webp' &&
        type !== 'audio' &&
        type !== 'caption')
    ) {
      return;
    }
    assets[id] = { ...value, id, type } as EditorStarterAsset;
  });

  // payload 가 저장 전(saved=false, 자동 생성) 상태일 때만, video item 의 배치를
  // clipInfos 의 최신 clip rect 로 보정한다. avatar placement 변경이 clipInfos 에는
  // 반영됐지만 먼저 생성된 payload editor item 에는 풀스크린(stale)으로 남는 경우 교정.
  const payloadSaved = payload?.['saved'] === true;
  const clipRectByContentId = new Map<string, ClipLayout>();
  if (!payloadSaved) {
    normalizeClipInfos(editorData).forEach((clip) => {
      const contentId = (clip as { contentId?: unknown }).contentId;
      if (contentId == null) return;
      const rect = readClipLayout(clip as ContentClipInfo);
      if (rect) {
        clipRectByContentId.set(String(contentId), rect);
      }
    });
  }

  const items: Record<string, EditorStarterItem> = {};
  Object.entries(itemSeeds).forEach(([key, value]) => {
    if (!isRecordValue(value)) return;
    const id = readStringValue(value.id) ?? key;
    const type = readStringValue(value.type) as EditorItemType | null;
    if (!id || !type) return;
    let item = { ...value, id, type } as EditorStarterItem;
    if (type === 'video') {
      const assetId = readStringValue((value as { assetId?: unknown }).assetId);
      const rect =
        !payloadSaved && assetId ? clipRectByContentId.get(assetId) : undefined;
      if (rect) {
        item = {
          ...item,
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        };
      }
    }
    items[id] = item;
  });

  if (Object.keys(items).length === 0) {
    return null;
  }

  const tracks: TrackType[] = Array.isArray(trackSeeds)
    ? trackSeeds.flatMap((seed, index): TrackType[] => {
        if (!isRecordValue(seed)) return [];
        const itemIds = Array.isArray(seed.items)
          ? seed.items.flatMap((itemId) => {
              const id = readStringValue(itemId);
              return id && items[id] ? [id] : [];
            })
          : [];
        if (itemIds.length === 0) return [];
        const firstItemId = itemIds[0];
        if (!firstItemId) return [];
        const metadata = isRecordValue(seed.metadata) ? seed.metadata : {};
        const type =
          (readStringValue(metadata.type) as EditorItemType | null) ??
          items[firstItemId]?.type;
        if (!type) return [];
        const isMainTrack = metadata.isMainTrack === true;
        return [
          {
            id:
              readStringValue(seed.id) ??
              `agent-workflow-source-track-${index}`,
            items: itemIds,
            hidden: seed.hidden === true,
            muted: seed.muted === true,
            locked: seed.locked === true,
            magnetic: seed.magnetic === true,
            zone: seed.zone === 'audio' ? 'audio' : getZoneForItemType(type),
            metadata: {
              isMainTrack,
              trackType: isMainTrack ? 'main' : null,
              type,
            },
          },
        ];
      })
    : [];

  const fallbackTracks =
    tracks.length > 0
      ? tracks
      : Object.values(items)
          .reduce(
            (acc, item) => {
              const group = acc.find((track) => track.type === item.type);
              if (group) {
                group.items.push(item);
              } else {
                acc.push({ type: item.type, items: [item] });
              }
              return acc;
            },
            [] as Array<{ type: EditorItemType; items: EditorStarterItem[] }>,
          )
          .flatMap(({ type, items: groupItems }) => {
            const track = createTrack(groupItems, type);
            return track ? [track] : [];
          });

  const transitions: Record<string, TransitionItem> = isRecordValue(
    transitionSeeds,
  )
    ? Object.fromEntries(
        Object.entries(transitionSeeds).flatMap(([key, value]) => {
          if (!isRecordValue(value)) return [];
          const id = readStringValue(value.id) ?? key;
          if (
            value.type !== 'transition' ||
            typeof value.transitionType !== 'string' ||
            typeof value.durationInFrames !== 'number' ||
            typeof value.sourceItemId !== 'string' ||
            typeof value.targetItemId !== 'string' ||
            typeof value.trackId !== 'string' ||
            typeof value.from !== 'number'
          ) {
            return [];
          }
          return [[id, { ...value, id } as TransitionItem]];
        }),
      )
    : {};

  const payloadDurationInFrames = readFiniteNumber(payload?.durationInFrames);
  const itemEndFrame = Math.max(
    0,
    ...Object.values(items).map((item) => item.from + item.durationInFrames),
  );
  return {
    assets,
    items,
    tracks: normalizeTimelineTracksForLayering({
      tracks: fallbackTracks,
      items,
      transitions,
    }),
    transitions,
    durationInFrames: Math.max(
      1,
      Math.round(payloadDurationInFrames ?? 0),
      itemEndFrame,
    ),
    backgroundColor: readStringValue(payload?.backgroundColor) ?? undefined,
  };
};

export const createCodeItemsFromEditorSource = ({
  editorData,
  compositionWidth,
  compositionHeight,
  fps,
}: {
  editorData?: EditorDataResponse;
  compositionWidth: number;
  compositionHeight: number;
  fps: number;
}): CodeItem[] => {
  const payload = editorData?.clipEditorInfos?.payload as
    | Record<string, unknown>
    | undefined;
  const seeds = payload?.[AGENT_WORKFLOW_CODE_ITEM_PAYLOAD_KEY];
  if (!Array.isArray(seeds)) return [];

  return seeds.flatMap((seed, index): CodeItem[] => {
    if (!seed || typeof seed !== 'object') return [];
    const record = seed as Record<string, unknown>;
    const code = readStringValue(record.code);
    if (!code) return [];

    const fromFrame = readFiniteNumber(record.from);
    const startSec = Math.max(0, readFiniteNumber(record.startSec) ?? 0);
    const durationInFramesFromSeed = readFiniteNumber(record.durationInFrames);
    const durationSec = readFiniteNumber(record.durationSec);
    const sourceFps = readFiniteNumber(record.fps) ?? fps;
    const width = Math.max(
      1,
      Math.round(readFiniteNumber(record.width) ?? compositionWidth),
    );
    const height = Math.max(
      1,
      Math.round(readFiniteNumber(record.height) ?? compositionHeight),
    );
    const durationInFrames = Math.max(
      1,
      Math.round(
        durationInFramesFromSeed ??
          (durationSec && durationSec > 0 ? durationSec : 1) * fps,
      ),
    );

    return [
      {
        id: `agent-workflow-code-${index + 1}`,
        type: 'code',
        from: Math.max(0, Math.round(fromFrame ?? startSec * fps)),
        durationInFrames,
        top: 0,
        left: 0,
        width,
        height,
        opacity: 1,
        rotation: 0,
        isDraggingInTimeline: false,
        code,
        title:
          readStringValue(record.title) ?? `Agent Workflow Code ${index + 1}`,
        boundingMode:
          record.boundingMode === 'bounded' ? 'bounded' : 'fullscreen',
        fadeInDurationInSeconds: 0,
        fadeOutDurationInSeconds: 0,
        timingOverrides: null,
        timeline: null,
        fps: sourceFps,
        durationSec:
          durationSec && durationSec > 0
            ? durationSec
            : durationInFrames / Math.max(1, sourceFps),
        templateId: readStringValue(record.templateId) ?? undefined,
        playbackContract: readStringValue(record.playbackContract) ?? undefined,
        alphabetCase: readStringValue(record.alphabetCase) ?? undefined,
        rendererSourceHash:
          readStringValue(record.rendererSourceHash) ?? undefined,
        rendererAssetPath:
          readStringValue(record.rendererAssetPath) ?? undefined,
        runtimeSourceHash:
          readStringValue(record.runtimeSourceHash) ?? undefined,
        rendererVersion: readStringValue(record.rendererVersion) ?? undefined,
        authoringStyle: isRecordValue(record.authoringStyle)
          ? record.authoringStyle
          : undefined,
        authoringSpec: isRecordValue(record.authoringSpec)
          ? (record.authoringSpec as CodeItem['authoringSpec'])
          : undefined,
      },
    ];
  });
};

// 이미지 renderMeta 생성
export const createImageRenderMeta = ({
  clip,
}: {
  clip: ImageClipInfo;
}): ImageItemRenderMeta | undefined => {
  const { start, end, clipType, id, videoStart, videoEnd, linkId } = clip;

  if (id && linkId) {
    return {
      id,
      clipType,
      start: start ?? 0,
      end: end ?? 0,
      videoStart: videoStart ?? 0,
      videoEnd: videoEnd ?? 0,
      linkId,
    };
  }

  return undefined;
};

export const createImageItemFromClip = ({
  clip,
  fps,
  compositionWidth,
  compositionHeight,
  contentInfo,
  isMainTrack,
  renderMeta,
}: {
  clip: ImageClipInfo;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  contentInfo?: ContentInfo;
  isMainTrack: boolean;
  renderMeta?: ImageItemRenderMeta;
}): { item: ImageItem; asset: ImageAsset } | null => {
  const contentId = clip.contentId;
  if (!contentId) return null;
  if (clip.videoStart == null && clip.videoEnd == null) return null;

  const clipDurationMs =
    clip.videoEnd != null && clip.videoStart != null
      ? clip.videoEnd - clip.videoStart
      : null;
  const fallbackDurationInSeconds =
    clipDurationMs != null && clipDurationMs > 0 ? clipDurationMs / 1000 : null;

  const { from, durationInFrames } = calculateFrameRange({
    fps,
    startMs: clip.videoStart,
    endMs: clip.videoEnd,
    fallbackDurationInSeconds,
  });

  // 미분석 콘텐츠(width/height 0)는 ?? 를 통과해 0/0 계산으로 아이템 치수가
  // NaN 이 되어 렌더가 깨진다(율동 배경 이미지 실측) — 유한 양수일 때만
  // 콘텐츠 치수를 신뢰하고 아니면 캔버스 치수로 폴백.
  const rawMediaWidth = (contentInfo as ContentInfo & { width?: number })
    ?.width;
  const rawMediaHeight = (contentInfo as ContentInfo & { height?: number })
    ?.height;
  const mediaWidth =
    typeof rawMediaWidth === 'number' &&
    Number.isFinite(rawMediaWidth) &&
    rawMediaWidth > 0
      ? rawMediaWidth
      : compositionWidth;
  const mediaHeight =
    typeof rawMediaHeight === 'number' &&
    Number.isFinite(rawMediaHeight) &&
    rawMediaHeight > 0
      ? rawMediaHeight
      : compositionHeight;

  const fitDimensions = calculateMediaDimensionsForCanvas({
    mediaWidth,
    mediaHeight,
    containerWidth: compositionWidth,
    containerHeight: compositionHeight,
    dropPosition: null,
  });
  const { width, height, top, left } = readClipLayout(clip) ?? fitDimensions;

  const asset: ImageAsset = {
    id: String(contentId),
    contentId,
    type: 'image',
    filename: createFilename(contentId, contentInfo),
    remoteUrl:
      clip.contentPublicUri ??
      clip.contentUri ??
      (contentInfo as ContentInfo & { publicUrl?: string })?.publicUrl ??
      null,
    remoteFileKey: null,
    size: contentInfo?.fileSize ?? 0,
    mimeType: guessMimeType(CLIP_TYPES.IMAGE),
    width,
    height,
    resolution:
      (contentInfo as ContentInfo & { resolution?: DtoResolution })
        ?.resolution ?? null,
  };

  const item: ImageItem = {
    id: generateRandomId(),
    durationInFrames,
    from,
    type: 'image',
    assetId: asset.id,
    isDraggingInTimeline: false,
    top,
    left,
    width,
    height,
    opacity: 1,
    borderRadius: 0,
    rotation: 0,
    keepAspectRatio: true,
    fadeInDurationInSeconds: 0,
    fadeOutDurationInSeconds: 0,
    isMainTrack,
    renderMeta,
    cropLeft: 0,
    cropTop: 0,
    cropRight: 0,
    cropBottom: 0,
  };

  return { item, asset };
};

// 비디오 renderMeta 생성
export const createVideoRenderMeta = ({
  clip,
}: {
  clip: VideoClipInfo;
}): VideoItemRenderMeta | undefined => {
  const { start, end, clipType, id, videoStart, videoEnd, linkId } = clip;

  if (id && linkId) {
    return {
      id,
      clipType,
      start: start ?? 0,
      end: end ?? 0,
      videoStart: videoStart ?? 0,
      videoEnd: videoEnd ?? 0,
      linkId,
    };
  }

  return undefined;
};

export const createVideoItemFromClip = ({
  clip,
  fps,
  compositionWidth,
  compositionHeight,
  contentInfo,
  projectType,
  isMainTrack,
  renderMeta,
  reframeMeta,
}: {
  clip: ContentClipInfo;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  contentInfo?: ContentInfo;
  projectType: DtoProjectType;
  isMainTrack: boolean;
  renderMeta?: VideoItemRenderMeta;
  reframeMeta?: ReframeMeta;
}): {
  item: VideoItem;
  asset: VideoAsset;
} | null => {
  const contentId = clip.contentId;
  if (!contentId) return null;

  const timelineStartInSeconds = msToSeconds(clip.videoStart ?? 0);
  const timelineEndInSeconds =
    clip.videoEnd != null ? msToSeconds(clip.videoEnd) : null;
  const videoStartFromInSeconds = msToSeconds(clip.start);
  const videoEndFromInSeconds = clip.end != null ? msToSeconds(clip.end) : null;

  const clipDurationFromSource =
    videoEndFromInSeconds != null
      ? videoEndFromInSeconds - videoStartFromInSeconds
      : null;
  const clipDurationFromTimeline =
    timelineEndInSeconds != null
      ? timelineEndInSeconds - timelineStartInSeconds
      : null;

  const clipDurationForItem =
    clipDurationFromTimeline && clipDurationFromTimeline > 0
      ? clipDurationFromTimeline
      : clipDurationFromSource && clipDurationFromSource > 0
        ? clipDurationFromSource
        : null;

  if (!clipDurationForItem) {
    return null;
  }

  // 프레임 경계를 서버 타임라인과 동일하게 맞춰 클립 전환 시 갭이 생기지 않도록 한다.
  const { from, durationInFrames } = calculateFrameRange({
    fps,
    startMs: clip.videoStart,
    endMs: clip.videoEnd,
    fallbackDurationInSeconds: clipDurationForItem,
  });

  const sourceDimensionClip = clip as SourceVideoDimensionClip;
  const rawMediaWidth =
    readPositiveDimension(
      (contentInfo as ContentInfo & { width?: number })?.width,
      sourceDimensionClip.sourceVideoWidth,
      sourceDimensionClip.source_video_width,
      sourceDimensionClip.videoWidth,
      sourceDimensionClip.video_width,
      sourceDimensionClip.sourceWidth,
      sourceDimensionClip.source_width,
      sourceDimensionClip.naturalWidth,
      sourceDimensionClip.natural_width,
      compositionWidth,
    ) ?? compositionWidth;
  const rawMediaHeight =
    readPositiveDimension(
      (contentInfo as ContentInfo & { height?: number })?.height,
      sourceDimensionClip.sourceVideoHeight,
      sourceDimensionClip.source_video_height,
      sourceDimensionClip.videoHeight,
      sourceDimensionClip.video_height,
      sourceDimensionClip.sourceHeight,
      sourceDimensionClip.source_height,
      sourceDimensionClip.naturalHeight,
      sourceDimensionClip.natural_height,
      compositionHeight,
    ) ?? compositionHeight;
  const { width: mediaWidth, height: mediaHeight } =
    normalizeMediaDimensionsForEditor({
      width: rawMediaWidth,
      height: rawMediaHeight,
      compositionWidth,
      compositionHeight,
    });

  const fitDimensions = calculateMediaDimensionsForCanvas({
    mediaWidth,
    mediaHeight,
    containerWidth: compositionWidth,
    containerHeight: compositionHeight,
    dropPosition: null,
  });

  // 리프레임 활성 시 비디오가 composition 전체를 채우도록 (동적 crop이 비율을 처리)
  const clipLayout = readClipLayout(clip);
  const { width, height, top, left } = reframeMeta
    ? { width: compositionWidth, height: compositionHeight, top: 0, left: 0 }
    : (clipLayout ?? fitDimensions);
  const shouldMuteSourceAudio = clip.sourceAudioMuted === true;

  const contentDurationInSeconds =
    typeof contentInfo?.duration === 'number'
      ? contentInfo.duration / 1000
      : null;
  const assetDurationCandidates = [
    contentDurationInSeconds,
    videoEndFromInSeconds,
    clipDurationFromSource && clipDurationFromSource > 0
      ? videoStartFromInSeconds + clipDurationFromSource
      : null,
    videoStartFromInSeconds + clipDurationForItem,
  ].filter(
    (candidate): candidate is number =>
      typeof candidate === 'number' &&
      Number.isFinite(candidate) &&
      candidate > 0,
  );
  let assetDurationInSeconds: number;
  if (contentDurationInSeconds && contentDurationInSeconds > 0) {
    assetDurationInSeconds = contentDurationInSeconds;
  } else {
    assetDurationInSeconds =
      assetDurationCandidates.length > 0
        ? Math.max(...assetDurationCandidates)
        : clipDurationForItem;
  }

  // 아바타 오버레이처럼 투명(알파) 클립은 배경이 크로마키된 투명 WebM 을 에디터 소스로 쓴다.
  // (video-layer 가 asset.hasAlpha=true 면 OffthreadVideo 를 transparent 로 렌더하는데,
  //  소스가 알파 채널 있는 WebM 이어야 배경이 비치고, 불투명 MP4 면 배경색이 그대로 남는다.)
  const alphaClip = clip as VideoClipInfo;
  const isAlphaVideo = alphaClip.hasAlpha === true && !!alphaClip.alphaVideoUrl;
  const asset: VideoAsset = {
    id: String(contentId),
    contentId,
    type: 'video',
    durationInSeconds: assetDurationInSeconds,
    hasAudioTrack: true,
    filename: createFilename(contentId, contentInfo),
    remoteUrl:
      (isAlphaVideo ? alphaClip.alphaVideoUrl : null) ??
      clip.contentPublicUri ??
      clip.contentUri ??
      (contentInfo as ContentInfo & { publicUrl?: string })?.publicUrl ??
      null,
    remoteFileKey: null,
    size: contentInfo?.fileSize ?? 0,
    mimeType: isAlphaVideo ? 'video/webm' : guessMimeType(CLIP_TYPES.VIDEO),
    width: mediaWidth,
    height: mediaHeight,
    resolution:
      (contentInfo as ContentInfo & { resolution?: DtoResolution })
        ?.resolution ?? null,
    // alphaVideoUrl(투명 WebM)이 실제로 있을 때만 transparent 처리한다. 불투명 영상이
    // hasAlpha=true 로 오플래그되면 OffthreadVideo 가 느린 알파(PNG) 경로로 빠져 렌더가
    // 행에 걸린다 (GS 모르간 셔츠 MXF 사례 — content 에 알파 없는데 transparent=true).
    hasAlpha: isAlphaVideo,
    // 알파 WebM 은 다중 해상도 변환(MP4 variants)이 없으므로 variants 를 비워
    // 에디터가 불투명 MP4 variant 로 폴백하지 않게 한다.
    variants: isAlphaVideo
      ? null
      : (clip.variants ?? contentInfo?.variants ?? null),
  };

  const item: VideoItem = {
    id: generateRandomId(),
    durationInFrames,
    videoStartFromInSeconds,
    from,
    type: 'video',
    assetId: asset.id,
    isDraggingInTimeline: false,
    top,
    left,
    width,
    height,
    opacity: 1,
    borderRadius: 0,
    rotation: 0,
    /* MTS일 경우 볼륨은 0 */
    decibelAdjustment:
      shouldMuteSourceAudio || projectType === DtoProjectType.commerce
        ? MIN_VOLUME_DB
        : 0,
    playbackRate: 1,
    audioFadeInDurationInSeconds: 0,
    audioFadeOutDurationInSeconds: 0,
    fadeInDurationInSeconds: 0,
    fadeOutDurationInSeconds: 0,
    keepAspectRatio: true,
    isMainTrack,
    renderMeta,
    cropLeft: 0,
    cropTop: 0,
    cropRight: 0,
    cropBottom: 0,
    ...(reframeMeta ? { reframeMeta } : {}),
    ...(shouldMuteSourceAudio ? { muteAudio: true } : {}),
    ...(typeof alphaClip.audioPlaybackMode === 'string'
      ? { audioPlaybackMode: alphaClip.audioPlaybackMode }
      : {}),
    // 알파 오버레이(아바타)는 item 에도 hasAlpha 를 실어, 브랜드 템플릿의
    // main-video 풀스크린 fit/reframe 대상에서 제외되도록 한다(placement 보존).
    ...(isAlphaVideo ? { hasAlpha: true } : {}),
    ...(typeof alphaClip.sourceRole === 'string'
      ? { sourceRole: alphaClip.sourceRole }
      : {}),
    // defer-baking(v2) 세그먼트 클립: 클립이 아이템보다 짧으면 반복 재생.
    ...(alphaClip.loop === true ? { loop: true } : {}),
    sourceVideoWidth: mediaWidth,
    sourceVideoHeight: mediaHeight,
  };

  return { item, asset };
};

export const createAudioRenderMeta = ({
  clip,
}: {
  clip: AudioClipInfo;
}): AudioItemRenderMeta | undefined => {
  const {
    start,
    end,
    clipType,
    id,
    videoStart,
    videoEnd,
    linkId,
    sceneId,
    voiceId,
    vendorModel,
    pitch,
    speedFactor,
    emotion,
    requestVoiceId,
  } = clip;

  if (id && linkId && sceneId) {
    return {
      id,
      clipType,
      start: start ?? 0,
      end: end ?? 0,
      videoStart: videoStart ?? 0,
      videoEnd: videoEnd ?? 0,
      linkId,
      sceneId,
      voiceId,
      vendorModel,
      pitch,
      speedFactor,
      emotion,
      requestVoiceId,
    };
  }
  return undefined;
};

export const createAudioItemFromClip = ({
  clip,
  fps,
  contentInfo,
  isMainTrack,
  renderMeta,
}: {
  clip: ContentClipInfo;
  fps: number;
  contentInfo?: ContentInfo;
  isMainTrack: boolean;
  renderMeta?: AudioItemRenderMeta;
}): {
  item: AudioItem;
  asset: AudioAsset;
} | null => {
  const contentId = clip.contentId;
  if (!contentId) return null;

  const timelineStartInSeconds = msToSeconds(clip.videoStart ?? 0);
  const timelineEndInSeconds =
    clip.videoEnd != null ? msToSeconds(clip.videoEnd) : null;
  const audioStartFromInSeconds = msToSeconds(clip.start);
  const audioEndFromInSeconds = clip.end != null ? msToSeconds(clip.end) : null;

  const clipDurationFromSource =
    audioEndFromInSeconds != null
      ? audioEndFromInSeconds - audioStartFromInSeconds
      : null;
  const clipDurationFromTimeline =
    timelineEndInSeconds != null
      ? timelineEndInSeconds - timelineStartInSeconds
      : null;

  const clipDurationForItem =
    clipDurationFromTimeline && clipDurationFromTimeline > 0
      ? clipDurationFromTimeline
      : clipDurationFromSource && clipDurationFromSource > 0
        ? clipDurationFromSource
        : null;

  if (!clipDurationForItem) {
    return null;
  }

  // 비디오와 동일하게 타임라인 경계 기반으로 프레임을 산출해 오디오가 밀리거나 빨라지지 않도록 한다.
  const { from, durationInFrames } = calculateFrameRange({
    fps,
    startMs: clip.videoStart,
    endMs: clip.videoEnd,
    fallbackDurationInSeconds: clipDurationForItem,
  });

  const contentDurationInSeconds =
    typeof contentInfo?.duration === 'number'
      ? contentInfo.duration / 1000
      : null;
  const assetDurationCandidates = [
    contentDurationInSeconds,
    audioEndFromInSeconds,
    clipDurationFromSource && clipDurationFromSource > 0
      ? audioStartFromInSeconds + clipDurationFromSource
      : null,
    audioStartFromInSeconds + clipDurationForItem,
  ].filter(
    (candidate): candidate is number =>
      typeof candidate === 'number' &&
      Number.isFinite(candidate) &&
      candidate > 0,
  );
  // contentDurationInSeconds가 clip의 실제 사용 범위(audioStartFromInSeconds + duration)보다 작을 수 있으므로
  // 모든 후보 중 max를 취해 asset duration이 clip 재생에 충분하도록 보장한다
  const assetDurationInSeconds =
    assetDurationCandidates.length > 0
      ? Math.max(...assetDurationCandidates)
      : clipDurationForItem;

  const asset: AudioAsset = {
    id: String(contentId),
    contentId,
    type: 'audio',
    durationInSeconds: assetDurationInSeconds,
    filename: createFilename(contentId, contentInfo),
    remoteUrl:
      clip.contentPublicUri ??
      clip.contentUri ??
      (contentInfo as ContentInfo & { publicUrl?: string })?.publicUrl ??
      null,
    remoteFileKey: null,
    size: contentInfo?.fileSize ?? 0,
    mimeType: guessMimeType(CLIP_TYPES.AUDIO),
    resolution:
      (contentInfo as ContentInfo & { resolution?: DtoResolution })
        ?.resolution ?? null,
  };

  const item: AudioItem = {
    id: generateRandomId(),
    durationInFrames,
    audioStartFromInSeconds,
    from,
    type: 'audio',
    top: 0,
    left: 0,
    width: 100,
    height: 100,
    opacity: 1,
    decibelAdjustment: 0,
    playbackRate: 1,
    audioFadeInDurationInSeconds: 0,
    audioFadeOutDurationInSeconds: 0,
    assetId: asset.id,
    isDraggingInTimeline: false,
    isMainTrack,
    renderMeta,
  };

  return { item, asset };
};

// alignWords를 클립 상대 시각으로 정규화한다.
// 계약(ai-api sync_align_words): alignWords는 이미 클립 시작(clip.start) 기준 상대 시각이며,
// 첫 단어 앞의 무음(수십 ms~수 초)이 start에 그대로 담겨 온다. 여기서 "첫 단어=0"으로
// 재기준화하면 그 무음만큼 자막 전체가 상시 선행하는 버그가 된다 (2026-08-24 실측:
// 클립별 30~1,560ms 선행). 따라서 상대 데이터는 그대로 두고, 클립 길이를 초과하는
// 값(=절대 시각으로 온 비정상/구형 데이터)일 때만 첫 단어 기준으로 재기준화한다.
const normalizeAlignWordsToVideoBoundary = (
  words: AlignWord[],
  clipDurationMs?: number | null,
): AlignWord[] => {
  if (!Array.isArray(words) || words.length === 0) {
    return [];
  }

  const validStarts = words
    .map((word) => toValidNumber(word?.start))
    .filter((value): value is number => value !== null);
  const minStart = validStarts.length > 0 ? Math.min(...validStarts) : 0;
  const isAbsoluteTiming =
    clipDurationMs != null && clipDurationMs > 0 && minStart > clipDurationMs;
  const baseOffset = isAbsoluteTiming ? minStart : 0;

  let previousEnd = 0;

  return words.map((word, index) => {
    const rawStart = toValidNumber(word?.start);
    const normalizedStart =
      rawStart !== null
        ? Math.max(0, Math.round(rawStart - baseOffset))
        : index === 0
          ? 0
          : previousEnd;

    const rawEnd = toValidNumber(word?.end);
    let normalizedEnd =
      rawEnd !== null
        ? Math.max(0, Math.round(rawEnd - baseOffset))
        : normalizedStart;

    if (normalizedEnd <= normalizedStart) {
      normalizedEnd = normalizedStart + MIN_CAPTION_WORD_DURATION_MS;
    }

    const nextWord: AlignWord = {
      ...word,
      start: normalizedStart,
      end: normalizedEnd,
    };

    previousEnd = normalizedEnd;
    return nextWord;
  });
};

// 캡션과 연결된 비디오 clip 정보를 renderMeta 형태로 정리
// 캡션 클립에서 caption asset에 필요한 메타 데이터를 수집한다.
export const createCaptionRenderMeta = ({
  clip,
}: {
  clip: CaptionClipInfo;
}): CaptionMetadataType | undefined => {
  const { id, linkId, sceneId } = clip;

  if (id == null || linkId == null || sceneId == null) {
    return undefined;
  }

  return {
    clipType: CLIP_TYPES.CAPTION,
    clipId: id,
    linkId,
    sceneId,
    videoStart: toValidNumber(clip.videoStart) ?? 0,
    videoEnd: toValidNumber(clip.videoEnd) ?? 0,
  };
};

const buildCaptionsFromClip = ({
  clip,
  timelineStartMs,
  timelineEndMs,
}: {
  clip: CaptionClipInfo;
  timelineStartMs: number;
  timelineEndMs?: number | null;
}): Caption[] => {
  // clipDurationMs=null(videoEnd 누락)이면 상대/절대 판별이 불가능해 재기준화하지 않는다 —
  // 현재 확인된 모든 생산 경로는 videoEnd 를 채워 보내므로 의도된 트레이드오프.
  const clipDurationMs =
    timelineEndMs != null && timelineEndMs > timelineStartMs
      ? timelineEndMs - timelineStartMs
      : null;
  const alignWords = normalizeAlignWordsToVideoBoundary(
    Array.isArray(clip.alignWords) ? clip.alignWords : [],
    clipDurationMs,
  );
  // segment의 \n을 공백으로 치환한다.
  // 줄바꿈은 CSS text-wrap: balance가 처리하므로 데이터에서 강제 줄바꿈을 제거한다.
  const segment = (clip.segment ?? '').replace(/\n/g, ' ');
  const captions: Caption[] = [];
  let cursor = 0;
  const absoluteTimelineEndMs =
    clipDurationMs != null ? timelineStartMs + clipDurationMs : null;

  alignWords.forEach((wordInfo, index) => {
    const rawWord = wordInfo?.word ?? '';
    if (!rawWord) {
      return;
    }

    const previousEnd =
      index === 0 ? 0 : (toValidNumber(alignWords[index - 1]?.end) ?? 0);
    const relativeStart = toValidNumber(wordInfo.start) ?? previousEnd;
    const relativeEnd = toValidNumber(wordInfo.end);
    const effectiveEnd =
      relativeEnd !== null && relativeEnd > relativeStart
        ? relativeEnd
        : relativeStart + MIN_CAPTION_WORD_DURATION_MS;

    const startMs = timelineStartMs + Math.max(0, Math.round(relativeStart));
    const endMs = timelineStartMs + Math.max(0, Math.round(effectiveEnd));

    const readSegmentPrefix = () => {
      let prefix = '';
      if (segment) {
        const located = segment.indexOf(rawWord, cursor);
        if (located !== -1) {
          prefix = segment.slice(cursor, located);
          cursor = located + rawWord.length;
        } else if (index > 0) {
          prefix = ' ';
        }
      } else if (index > 0) {
        prefix = ' ';
      }
      return prefix;
    };

    const prefix = readSegmentPrefix();
    if (absoluteTimelineEndMs != null && startMs >= absoluteTimelineEndMs) {
      return;
    }

    let safeEnd =
      endMs > startMs ? endMs : startMs + MIN_CAPTION_WORD_DURATION_MS;
    if (absoluteTimelineEndMs != null) {
      safeEnd = Math.min(safeEnd, absoluteTimelineEndMs);
    }
    if (safeEnd <= startMs) {
      return;
    }

    const mergedText = `${prefix}${rawWord}`;
    // Caption은 @remotion/captions의 외부 타입이라 직접 확장 불가.
    // speaker는 spread로 보존되도록 caption 객체에 동봉(extra property는 cloneCaption의
    // `...caption` spread로 자동 보존). downstream에서 (caption as Caption & {speaker?})로 접근.
    captions.push({
      text: mergedText,
      startMs,
      endMs: safeEnd,
      timestampMs: Math.round((startMs + safeEnd) / 2),
      confidence: null,
      ...(wordInfo.speaker ? { speaker: wordInfo.speaker } : {}),
    });
  });

  if (captions.length > 0 && cursor < segment.length) {
    const tail = segment.slice(cursor);
    if (tail) {
      const lastCaption = captions[captions.length - 1];
      if (lastCaption) lastCaption.text = `${lastCaption.text}${tail}`;
    }
  }

  const hasAlignedCaptionTiming = captions.length > 0;
  if (captions.length === 0) {
    const trimmed = segment.trim();
    if (!trimmed) {
      return [];
    }
    const fallbackStart = timelineStartMs;
    const fallbackEndCandidate =
      timelineEndMs != null && timelineEndMs > fallbackStart
        ? timelineEndMs
        : fallbackStart + 1;

    captions.push({
      text: segment,
      startMs: fallbackStart,
      endMs: fallbackEndCandidate,
      timestampMs: Math.round((fallbackStart + fallbackEndCandidate) / 2),
      confidence: null,
    });
  }

  // 정렬 단어가 없는 fallback 자막만 clip 끝까지 늘린다. 정렬 단어가 있으면
  // 마지막 단어의 발화 종료와 자막 item의 표시 종료를 분리해 tail을 보존한다.
  const lastCaption = captions[captions.length - 1];
  if (
    !hasAlignedCaptionTiming &&
    lastCaption &&
    timelineEndMs != null &&
    timelineEndMs > lastCaption.startMs &&
    lastCaption.endMs < timelineEndMs
  ) {
    lastCaption.endMs = timelineEndMs;
    lastCaption.timestampMs = Math.round(
      (lastCaption.startMs + timelineEndMs) / 2,
    );
  }

  return captions;
};

/** 화면에 띄울 번역 상태. `failed` 는 원문을 그대로 쓰므로 제외한다. */
const DISPLAYABLE_TRANSLATION_STATUSES: ReadonlySet<string> = new Set([
  'translated',
  'passthrough',
]);

/**
 * LTS 자막 번역 결과(cue)를 표시용 `Caption` 배열로 만든다.
 *
 * cue 의 start/end 는 캡션 시작 기준 상대 ms 라 `timelineStartMs` 를 더해 절대화한다.
 * 번역이 없거나 `failed`, 타겟 언어 미상, cue 가 비면 `null` 을 돌려 기존(원문) 경로를 그대로 태운다.
 */
const buildTranslatedCaptionsFromClip = ({
  clip,
  timelineStartMs,
}: {
  clip: CaptionClipInfo;
  timelineStartMs: number;
}): Caption[] | null => {
  const translation = clip.translation;
  if (!translation) return null;
  if (!DISPLAYABLE_TRANSLATION_STATUSES.has(translation.status)) return null;
  // 타겟 언어를 모르면 원문↔번역 전환의 기준이 없으므로 번역을 적용하지 않는다.
  if (!translation.targetLanguage) return null;

  const captions = buildCaptionsFromTranslationCues({
    cues: translation.cues,
    baseMs: timelineStartMs,
  });

  return captions.length > 0 ? captions : null;
};

const getCaptionAssetSize = (captions: Caption[]): number => {
  const payload = JSON.stringify(captions);

  if (typeof Buffer !== 'undefined') {
    return Buffer.byteLength(payload, 'utf-8');
  }

  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(payload).length;
  }

  return payload.length;
};

const estimateCaptionTextWidth = (
  text: string,
  fontSize: number,
  letterSpacing: number,
): number => {
  if (!text) return 0;

  const letterSpacingUnit = fontSize > 0 ? letterSpacing / fontSize : 0;
  let totalUnits = 0;

  for (const char of text) {
    totalUnits += estimateCharUnit(char, letterSpacingUnit);
  }

  return totalUnits * fontSize;
};

const applyBalancedLineBreakToCaptions = ({
  captions,
  maxWidth,
  fontSize,
  letterSpacing,
  maxLines,
}: {
  captions: Caption[];
  maxWidth: number;
  fontSize: number;
  letterSpacing: number;
  maxLines: number;
}): Caption[] => {
  if (captions.length <= 1 || maxLines < 2) return captions;

  const fullText = captions.map((c) => (c.text ?? '').trim()).join(' ');
  const totalWidth = estimateCaptionTextWidth(
    fullText,
    fontSize,
    letterSpacing,
  );

  if (totalWidth <= maxWidth) return captions;

  let bestBreakIndex = -1;
  let minDiff = Infinity;

  for (let i = 1; i < captions.length; i++) {
    const line1Text = captions
      .slice(0, i)
      .map((c) => (c.text ?? '').trim())
      .join(' ');
    const line2Text = captions
      .slice(i)
      .map((c) => (c.text ?? '').trim())
      .join(' ');

    const w1 = estimateCaptionTextWidth(line1Text, fontSize, letterSpacing);
    const w2 = estimateCaptionTextWidth(line2Text, fontSize, letterSpacing);

    if (w1 <= maxWidth && w2 <= maxWidth) {
      const diff = Math.abs(w1 - w2);
      if (diff < minDiff) {
        minDiff = diff;
        bestBreakIndex = i;
      }
    }
  }

  if (bestBreakIndex <= 0) {
    for (let i = 1; i < captions.length; i++) {
      const line1Text = captions
        .slice(0, i)
        .map((c) => (c.text ?? '').trim())
        .join(' ');
      const w1 = estimateCaptionTextWidth(line1Text, fontSize, letterSpacing);

      if (w1 > maxWidth) {
        bestBreakIndex = Math.max(1, i - 1);
        break;
      }
    }
  }

  if (bestBreakIndex <= 0) return captions;

  return captions.map((caption, idx) => {
    if (idx === bestBreakIndex - 1) {
      const text = caption.text ?? '';
      const trailingSpacesMatch = text.match(/[ \t]+$/);
      const trailingSpaces = trailingSpacesMatch?.[0] ?? '';
      const coreText = trailingSpaces
        ? text.slice(0, text.length - trailingSpaces.length)
        : text;

      if (coreText.endsWith('\n') || coreText.endsWith('\r')) {
        return caption;
      }

      return {
        ...caption,
        text: `${coreText}\n${trailingSpaces}`,
      };
    }
    return caption;
  });
};

export const createCaptionItemFromClip = async ({
  clip,
  fps,
  compositionWidth,
  compositionHeight,
  textStyleConfig,
  metadata,
  projectId,
  storyId,
}: {
  clip: CaptionClipInfo;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  textStyleConfig?: TextStyleConfig;
  metadata?: CaptionMetadataType;
  projectId?: number;
  storyId?: number;
}): Promise<{ item: CaptionsItem; asset: CaptionAsset } | null> => {
  const timelineStartMs = toValidNumber(clip.videoStart) ?? 0;
  const timelineEndMs = toValidNumber(clip.videoEnd);

  const rawCaptions = buildCaptionsFromClip({
    clip,
    timelineStartMs,
    timelineEndMs,
  });

  if (rawCaptions.length === 0) {
    return null;
  }

  // 클립 시작~첫 자막 사이 앞 무음 길이. captions-layer 가 이 값(captionSyncLeadMs)만큼
  // 첫 페이지 표시를 선행시키므로 자막 문장은 기존처럼 클립 시작부터 보이고, 단어
  // 하이라이트는 발화 시각을 그대로 유지한다 — 표시 시각과 강조 시각의 분리.
  // videoEnd 가 없으면 상대/절대 판별 없이 통과한 단어라 무음 계산도 신뢰할 수 없어
  // 세팅하지 않는다 (buildCaptionsFromClip 의 clipDurationMs=null 트레이드오프와 동일선).
  const captionSyncLeadMs =
    timelineEndMs != null && timelineEndMs > timelineStartMs
      ? Math.max(
          0,
          Math.round(
            (rawCaptions[0]?.startMs ?? timelineStartMs) - timelineStartMs,
          ),
        )
      : 0;

  const lastCaptionEndMs =
    rawCaptions[rawCaptions.length - 1]?.endMs ??
    timelineEndMs ??
    timelineStartMs + 1;

  const fallbackDurationInSeconds =
    lastCaptionEndMs > timelineStartMs
      ? (lastCaptionEndMs - timelineStartMs) / 1000
      : undefined;

  const { from, durationInFrames } = calculateFrameRange({
    fps,
    startMs: timelineStartMs,
    endMs:
      timelineEndMs != null && timelineEndMs > timelineStartMs
        ? timelineEndMs
        : lastCaptionEndMs,
    fallbackDurationInSeconds,
  });

  if (durationInFrames <= 0) {
    return null;
  }

  const fontFamily = textStyleConfig?.captionFontFamily ?? DEFAULT_FONT_FAMILY;
  const fontSize = textStyleConfig?.captionFontSize ?? 80;
  const lineHeight = textStyleConfig?.captionLineHeight ?? 1.2;
  const letterSpacing = textStyleConfig?.captionLetterSpacing ?? 0;
  const maxAllowedWidth = Math.max(1, Math.round(compositionWidth * 0.9));
  const maxLines = DEFAULT_CAPTION_MAX_LINES;

  // 줄바꿈 배분은 CSS text-wrap: balance가 처리하므로
  // applyBalancedLineBreakToCaptions 호출을 제거한다.
  const withLeadingSpace = (caption: Caption) => {
    const text = caption.text ?? '';
    return {
      ...caption,
      text: text.startsWith(' ') ? text : ` ${text}`,
    };
  };

  const captions = rawCaptions.map(withLeadingSpace);

  // LTS 자막 번역이 붙어 있으면 번역문을 표시본으로 쓰고 원문은 asset 에 남긴다.
  const rawTranslatedCaptions = buildTranslatedCaptionsFromClip({
    clip,
    timelineStartMs,
  });
  const translatedCaptions =
    rawTranslatedCaptions?.map(withLeadingSpace) ?? null;
  const translatedLanguage = clip.translation?.targetLanguage ?? null;
  const displayCaptions = translatedCaptions ?? captions;

  const color = textStyleConfig?.captionColor ?? '#FFFFFF';
  const strokeWidth = textStyleConfig?.captionStrokeWidth ?? 0;
  const strokeColor = textStyleConfig?.captionStrokeColor ?? '#000000';
  const yRatio = textStyleConfig?.captionYRatio ?? CAPTION_Y_RATIO;
  const clipLayout = readClipLayout(clip);

  // 실제 캡션 텍스트의 최대 폭 계산 (번역 표시 중이면 번역 텍스트 기준)
  const fullCaptionText = displayCaptions
    .map((c) => (c.text ?? '').trim())
    .join(' ');
  const estimatedTextWidth = estimateCaptionTextWidth(
    fullCaptionText,
    fontSize,
    letterSpacing,
  );

  // 텍스트 실제 폭과 최대 허용 폭 중 작은 값 + 여유 공간
  const textPadding = fontSize * 0.5; // 좌우 패딩
  const width =
    clipLayout?.width ??
    Math.max(
      1,
      Math.min(
        Math.round(estimatedTextWidth + textPadding * 2),
        maxAllowedWidth,
      ),
    );

  const left =
    clipLayout?.left ?? Math.max(0, Math.round((compositionWidth - width) / 2));

  // DOM 측정으로 실제 높이 계산, 불가능하면 추정값 사용
  const measuredHeight = measureAutoWrappedHeight({
    text: fullCaptionText,
    width,
    fontSize,
    lineHeight,
    letterSpacing,
    fontFamily,
    fontStyle: { variant: 'normal', weight: '400' },
  });

  // 측정된 높이가 없거나 0 이하면 추정값 사용
  const estimatedHeight = Math.round(fontSize * lineHeight * maxLines);
  const height =
    clipLayout?.height ??
    Math.max(
      1,
      measuredHeight && measuredHeight > 0 ? measuredHeight : estimatedHeight,
    );

  const centerY = Math.round(compositionHeight * yRatio);
  const preferredTop = centerY - Math.round(height / 2);
  const maxTop = Math.max(0, compositionHeight - height);
  const top = clipLayout?.top ?? Math.min(Math.max(preferredTop, 0), maxTop);

  // 방향은 실제로 표시할 텍스트 기준 — 번역 표시 중이면 번역문으로 판정한다.
  const directionSourceText = translatedCaptions
    ? fullCaptionText
    : (clip.segment ?? '');
  const direction = stringSeemsRightToLeft(directionSourceText) ? 'rtl' : 'ltr';

  // 다른 프로젝트/스토리에서 동일 clip.id가 재사용되더라도 캐시 키가 겹치지 않도록 식별자를 확장한다.
  const captionIdPart =
    clip.id != null ? `${clip.id}` : `${generateRandomId()}`;

  // 프로젝트/스토리 정보가 주어지면 `caption-c-{id}-{projectId}-{storyId}` 포맷을 유지한다
  const assetId = [
    'caption',
    'c',
    captionIdPart,
    projectId != null ? `${projectId}` : null,
    storyId != null ? `${storyId}` : null,
  ]
    .filter((part): part is string => part != null && part !== '')
    .join('-');

  const baseVideoEnd =
    timelineEndMs != null && timelineEndMs > timelineStartMs
      ? timelineEndMs
      : lastCaptionEndMs;

  // clip 정보에서 누락될 수 있는 메타 데이터를 안전한 기본값으로 채워 asset에 담는다.
  const captionMetadata: CaptionMetadataType = {
    clipType: CLIP_TYPES.CAPTION,
    clipId: metadata?.clipId ?? clip.id ?? -1,
    linkId: metadata?.linkId ?? clip.linkId ?? -1,
    sceneId: metadata?.sceneId ?? clip.sceneId ?? -1,
    videoStart: metadata?.videoStart ?? timelineStartMs,
    videoEnd: metadata?.videoEnd ?? baseVideoEnd,
  };

  // 원문 하이라이트 설정 — 번역 표시 중에는 item 에서 끄고 이 값을 asset 에 보관해 복원한다.
  const sourceHighlightVisible =
    isCaptionHighlightTimingEligible(clip) &&
    clip.captionStyle?.highlightVisible !== false;

  const asset: CaptionAsset = {
    id: assetId,
    type: 'caption',
    captions: displayCaptions,
    filename: `${assetId}.json`,
    remoteUrl: null,
    remoteFileKey: null,
    size: getCaptionAssetSize(displayCaptions),
    mimeType: 'application/json',
    ...captionMetadata,
    ...(translatedCaptions && translatedLanguage
      ? {
          captionLanguage: translatedLanguage,
          sourceCaptions: captions,
          translatedCaptions,
          translatedLanguage,
          sourceHighlight: {
            highlightVisible: sourceHighlightVisible,
            highlightEffect: clip.highlightEffect,
          },
        }
      : { captionLanguage: 'source' as const }),
  };

  const { clipId, ...renderMeta } = captionMetadata;

  const item: CaptionsItem = {
    id: generateRandomId(),
    type: 'captions',
    assetId: asset.id,
    from,
    durationInFrames,
    top,
    left,
    width,
    height,
    opacity: 1,
    isDraggingInTimeline: false,
    isMainTrack: true,
    rotation: 0,
    fontFamily,
    fontStyle: {
      variant: 'normal',
      weight: '400',
    },
    lineHeight,
    letterSpacing,
    fontSize,
    align: 'center',
    color,
    highlightColor:
      clip.highlightColor === undefined
        ? DEFAULT_CAPTION_HIGHLIGHT_COLOR
        : clip.highlightColor,
    // 번역 자막은 cue(문장) 단위라 단어 타이밍이 없다. 하이라이트를 켜 두면 단일 토큰이
    // 상시 active 로 잡혀 문장 전체가 강조색이 되므로 표시 중에는 항상 끈다.
    highlightEffect: translatedCaptions ? 'instant' : clip.highlightEffect,
    highlightVisible: translatedCaptions ? false : sourceHighlightVisible,
    strokeWidth,
    strokeColor,
    direction,
    pageDurationInMilliseconds: DEFAULT_CAPTION_PAGE_DURATION_MS,
    captionStartInSeconds: msToSeconds(timelineStartMs),
    ...(captionSyncLeadMs > 0 ? { captionSyncLeadMs } : {}),
    ...(clip.captionTimingPolicyVersion != null
      ? { captionTimingPolicyVersion: clip.captionTimingPolicyVersion }
      : {}),
    maxLines,
    fadeInDurationInSeconds: 0,
    fadeOutDurationInSeconds: 0,
    shadow: { ...defaultTextShadow },
    renderMeta: {
      id: clipId,
      ...renderMeta,
    },
  };

  return { item, asset };
};

export const createTitleRenderMeta = ({
  clip,
}: {
  clip: TitleClipInfo;
}): TitleItemRenderMeta | undefined => {
  const { clipType, title, selected, id, videoStart, videoEnd, linkId } = clip;

  if (id) {
    return {
      id,
      title,
      clipType,
      selected: !!selected,
      videoStart,
      videoEnd,
      linkId,
    };
  }
  return undefined;
};

// 타이틀 clip을 Remotion 텍스트 아이템으로 변환
export const createTextItemFromClip = async ({
  clip,
  fps,
  compositionWidth,
  compositionHeight,
  renderMeta,
  isMainTrack,
  text,
  yRatio,
  totalVideoDurationInFrames,
  textStyleConfig,
}: {
  clip: ClipInfo;
  fps: number;
  compositionWidth: number;
  compositionHeight: number;
  renderMeta?: CaptionItemRenderMeta | TitleItemRenderMeta;
  isMainTrack: boolean;
  text: string;
  yRatio: number;
  totalVideoDurationInFrames?: number;
  textStyleConfig?: TextStyleConfig;
}): Promise<TextItem | null> => {
  const { from, durationInFrames: baseDurationInFrames } = calculateFrameRange({
    fps,
    startMs: clip.videoStart,
    endMs: clip.videoEnd,
  });

  // 캡션/타이틀도 동일한 프레임 계산 로직을 따라 비디오 클립과 정합성을 유지한다
  const durationInFrames =
    isTitleClip(clip) && totalVideoDurationInFrames
      ? totalVideoDurationInFrames
      : baseDurationInFrames;

  if (!text) return null;

  const maxAllowedWidth = Math.max(1, Math.round(compositionWidth * 0.9));

  const targetFontSize = textStyleConfig?.titleFontSize ?? DEFAULT_FONT_SIZE;

  const targetLetterSpacing =
    textStyleConfig?.titleLetterSpacing ?? DEFAULT_TEXT_LETTER_SPACING;

  const targetLineHeight =
    textStyleConfig?.titleLineHeight ?? DEFAULT_TEXT_LINE_HEIGHT;

  const requestedFontFamily = textStyleConfig?.titleFontFamily;

  try {
    const item = (await createTextItem({
      xOnCanvas: Math.round(compositionWidth / 2),
      yOnCanvas: Math.round(compositionHeight * yRatio),
      from,
      text,
      align: 'center',
      duration: durationInFrames,
      renderMeta,
      isMainTrack,
    })) as TextItem;

    // 컨테이너 너비를 캔버스의 90%로 고정하고
    // 렌더링 측 word-break: keep-all로 어절 단위 줄바꿈을 처리한다
    const safeWidth = maxAllowedWidth;
    const targetFontFamily =
      requestedFontFamily ?? item.fontFamily ?? DEFAULT_FONT_FAMILY;
    const measuredHeight = measureAutoWrappedHeight({
      text,
      width: safeWidth,
      fontSize: targetFontSize,
      lineHeight: targetLineHeight,
      letterSpacing: targetLetterSpacing,
      fontFamily: targetFontFamily,
      fontStyle: item.fontStyle,
    });

    const safeHeight = measuredHeight ?? item.height;
    const preferredLeft =
      item.align === 'center'
        ? Math.round(compositionWidth / 2 - safeWidth / 2)
        : item.left;
    const maxLeft = Math.max(0, compositionWidth - safeWidth);
    const clampedLeft = Math.min(Math.max(preferredLeft, 0), maxLeft);
    // 캔버스 중앙 정렬을 유지하면서 세로 위치도 영역 안으로 눌러준다
    const preferredTop = item.top - Math.round((safeHeight - item.height) / 2);
    const maxTop = Math.max(0, compositionHeight - safeHeight);
    const clampedTop = Math.min(Math.max(preferredTop, 0), maxTop);

    /// 주입받은 style 적용
    item.fontSize = targetFontSize;

    item.color = textStyleConfig?.titleColor ?? item.color;

    item.fontFamily = targetFontFamily;

    item.lineHeight = targetLineHeight;

    item.letterSpacing = targetLetterSpacing;

    item.strokeWidth = textStyleConfig?.titleStrokeWidth ?? item.strokeWidth;

    item.strokeColor = textStyleConfig?.titleStrokeColor ?? item.strokeColor;

    item.text = text;

    return {
      ...item,
      text: text,
      width: safeWidth,
      height: safeHeight,
      left: clampedLeft,
      top: clampedTop,
    };
  } catch {
    return null;
  }
};
